/** Paginas publicas da area do trader: ligam as telas do @mtc/ui na API (spec 001, 4.2 a 4.5). */
import { ErroApi, TelaCpfErrado, TelaDefinirSenha, TelaEmailErrado, TelaEntrarTrader, TelaEsqueciSenha, mensagemDe, type DadosDoCadastro, type Prova, type RespostaDeEntrada, type RespostaDoCadastro, type RespostaDoCodigoDeSenha } from "@mtc/ui";
import { useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { marca } from "../config";
import { useSessao } from "../sessao";

const CODIGO_INVALIDO = "Código inválido, vencido ou já usado. Peça outro código.";
const SEM_CONEXAO = "Não foi possível falar com o servidor. Confira sua conexão e tente de novo.";
const MUITAS = "Muitas tentativas. Espere um pouco e tente de novo.";

function mensagem(err: unknown, porStatus: Record<number, string>, reserva: string) {
  if (err instanceof ErroApi) return porStatus[err.status] ?? (err.status === 429 ? MUITAS : reserva);
  return SEM_CONEXAO;
}

const ERRO_DE_ENTRADA = { 401: "CPF, e-mail ou senha incorretos.", 400: "Confira o CPF ou e-mail e a senha." };

/**
 * Entrar, criar a conta e criar a senha DENTRO do checkout (spec 045): as mesmas rotas e as mesmas frases das telas de
 * acesso, mas sem navegar. Quando a sessao abre (`entrou`), a pagina do checkout troca o bloco de conta pelo formulario
 * da compra, com tudo o que ja tinha sido escolhido. O `cadastro_id` fica aqui entre o pedido do codigo e a confirmacao.
 */
export function useContaNaCompra() {
  const { publica, entrou } = useSessao();
  const cadastro = useRef("");
  return {
    aoEntrar: async ({ identificador, senha }: { identificador: string; senha: string }): Promise<string | null> => {
      try {
        await entrou(await publica<RespostaDeEntrada>("/v1/auth/entrar", { identificador, senha }));
        return null;
      } catch (err) {
        return mensagem(err, ERRO_DE_ENTRADA, "Não conseguimos entrar agora. Tente de novo.");
      }
    },
    aoIniciarCadastro: async (d: DadosDoCadastro): Promise<RespostaDoCadastro> => {
      try {
        const r = await publica<{ cadastro_id: string; email_mascarado: string }>("/v1/cadastro/iniciar", { nome: d.nome, cpf: d.cpf, email: d.email, telefone: d.telefone });
        cadastro.current = r.cadastro_id;
        return { tipo: "enviado", emailMascarado: r.email_mascarado };
      } catch (err) {
        if (err instanceof ErroApi && err.status === 409 && err.corpo?.codigo === "CPF_JA_TEM_CONTA") return { tipo: "cpf-tem-conta", emailMascarado: typeof err.corpo.email_mascarado === "string" ? err.corpo.email_mascarado : null };
        if (err instanceof ErroApi && err.status === 409) return { tipo: "email-tem-conta" };
        return { tipo: "erro", erro: err instanceof ErroApi ? mensagemDe(err, "Não conseguimos enviar o código agora. Tente de novo.") : SEM_CONEXAO };
      }
    },
    aoConfirmarCadastro: async ({ codigo, senha }: { codigo: string; senha: string }): Promise<string | null> => {
      try {
        await entrou(await publica<RespostaDeEntrada>("/v1/cadastro/confirmar", { cadastro_id: cadastro.current, codigo, senha }));
        return null;
      } catch (err) {
        return err instanceof ErroApi ? mensagemDe(err, "Não conseguimos criar a conta agora. Tente de novo.") : SEM_CONEXAO;
      }
    },
    aoPedirCodigoDeSenha: async (identificador: string): Promise<RespostaDoCodigoDeSenha> => {
      try {
        const r = await publica<{ email_mascarado?: string }>("/v1/recuperacao/senha", { identificador });
        return { tipo: "enviado", emailMascarado: r.email_mascarado };
      } catch (err) {
        return { tipo: "erro", erro: mensagem(err, { 400: "Confira o CPF ou e-mail." }, "Não conseguimos enviar o código agora. Tente de novo.") };
      }
    },
    aoCriarSenha: async ({ identificador, codigo, novaSenha }: { identificador: string; codigo: string; novaSenha: string }): Promise<string | null> => {
      try {
        await entrou(await publica<RespostaDeEntrada>("/v1/recuperacao/senha/confirmar", { identificador, codigo, nova_senha: novaSenha }));
        return null;
      } catch (err) {
        return mensagem(err, { 400: CODIGO_INVALIDO }, "Não conseguimos salvar a senha agora. Tente de novo.");
      }
    },
  };
}

function useDepoisDeEntrar() {
  const { entrou } = useSessao();
  const navegar = useNavigate();
  const [params] = useSearchParams();
  return async (r: RespostaDeEntrada) => {
    await entrou(r);
    const volta = params.get("volta");
    navegar(volta && volta.startsWith("/") && !volta.startsWith("//") ? volta : "/", { replace: true });
  };
}

export function PaginaEntrar() {
  const { publica } = useSessao();
  const depois = useDepoisDeEntrar();
  const local = useLocation();
  // Voltou do checkout: quem comprou pela primeira vez ainda nao tem senha; o link do e-mail cria.
  const aviso = (local.state as { aviso?: string } | null)?.aviso ?? (new URLSearchParams(local.search).has("compra") ? "Compra confirmada. No primeiro acesso, crie a senha pelo link que enviamos pro seu e-mail." : undefined);
  const [erro, setErro] = useState<string>();
  const [carregando, setCarregando] = useState(false);
  return (
    <TelaEntrarTrader
      marca={marca()}
      aviso={aviso}
      erro={erro}
      carregando={carregando}
      aoEnviar={async ({ identificador, senha }) => {
        setCarregando(true);
        setErro(undefined);
        try {
          await depois(await publica<RespostaDeEntrada>("/v1/auth/entrar", { identificador, senha }));
        } catch (err) {
          setErro(mensagem(err, ERRO_DE_ENTRADA, "Não conseguimos entrar agora. Tente de novo."));
        } finally {
          setCarregando(false);
        }
      }}
    />
  );
}

export function PaginaEsqueciSenha() {
  const { publica } = useSessao();
  const depois = useDepoisDeEntrar();
  const [etapa, setEtapa] = useState<"identificar" | "codigo">("identificar");
  const [identificador, setIdentificador] = useState("");
  const [emailMascarado, setEmailMascarado] = useState<string>();
  const [erro, setErro] = useState<string>();
  const [carregando, setCarregando] = useState(false);
  const rodar = async (f: () => Promise<void>) => {
    setCarregando(true);
    setErro(undefined);
    try { await f(); } finally { setCarregando(false); }
  };
  return (
    <TelaEsqueciSenha
      marca={marca()}
      publico="area"
      etapa={etapa}
      emailMascarado={emailMascarado}
      erro={erro}
      carregando={carregando}
      aoPedirCodigo={(id) => rodar(async () => {
        try {
          const r = await publica<{ email_mascarado?: string }>("/v1/recuperacao/senha", { identificador: id });
          setIdentificador(id);
          setEmailMascarado(r.email_mascarado);
          setEtapa("codigo");
        } catch (err) {
          setErro(mensagem(err, { 400: "Confira o CPF ou e-mail." }, "Não conseguimos enviar o código agora. Tente de novo."));
        }
      })}
      aoConfirmar={({ codigo, novaSenha }) => rodar(async () => {
        try {
          await depois(await publica<RespostaDeEntrada>("/v1/recuperacao/senha/confirmar", { identificador, codigo, nova_senha: novaSenha }));
        } catch (err) {
          setErro(mensagem(err, { 400: CODIGO_INVALIDO }, "Não conseguimos salvar a senha agora. Tente de novo."));
        }
      })}
      aoTrocarDado={() => { setEtapa("identificar"); setErro(undefined); }}
    />
  );
}

export function PaginaEmailErrado() {
  const { publica } = useSessao();
  const depois = useDepoisDeEntrar();
  const [etapa, setEtapa] = useState<"dados" | "codigo">("dados");
  const [recuperacaoId, setRecuperacaoId] = useState("");
  const [mascarado, setMascarado] = useState<string>();
  const [erro, setErro] = useState<string>();
  const [carregando, setCarregando] = useState(false);
  const rodar = async (f: () => Promise<void>) => {
    setCarregando(true);
    setErro(undefined);
    try { await f(); } finally { setCarregando(false); }
  };
  return (
    <TelaEmailErrado
      marca={marca()}
      etapa={etapa}
      emailMascaradoNovo={mascarado}
      erro={erro}
      carregando={carregando}
      aoEnviarDados={({ cpf, prova, emailNovo }: { cpf: string; prova: Prova; emailNovo: string }) => rodar(async () => {
        try {
          const r = await publica<{ recuperacao_id: string; email_mascarado_novo: string }>("/v1/recuperacao/email/iniciar", { cpf, prova, email_novo: emailNovo });
          setRecuperacaoId(r.recuperacao_id);
          setMascarado(r.email_mascarado_novo);
          setEtapa("codigo");
        } catch (err) {
          setErro(mensagem(err, {
            404: "Não encontramos um pagamento com esses dados. Confira o CPF e o comprovante. Se o seu plano não teve pagamento pela plataforma, fale com o suporte.",
            409: "Este e-mail já está em outra conta. Use outro e-mail ou fale com o suporte.",
            429: "Muitas tentativas com este CPF. Tente de novo em uma hora.",
            400: "Confira os dados informados.",
          }, "Não conseguimos conferir agora. Tente de novo."));
        }
      })}
      aoConfirmar={({ codigo, novaSenha }) => rodar(async () => {
        try {
          await depois(await publica<RespostaDeEntrada>("/v1/recuperacao/email/confirmar", { recuperacao_id: recuperacaoId, codigo, nova_senha: novaSenha }));
        } catch (err) {
          setErro(mensagem(err, { 400: CODIGO_INVALIDO, 409: "Este e-mail já está em outra conta. Fale com o suporte." }, "Não conseguimos confirmar agora. Tente de novo."));
        }
      })}
      aoRecomecar={() => { setEtapa("dados"); setErro(undefined); }}
    />
  );
}

export function PaginaCpfErrado() {
  return <TelaCpfErrado marca={marca()} />;
}

export function PaginaDefinirSenha() {
  const { publica } = useSessao();
  const depois = useDepoisDeEntrar();
  const token = useSearchParams()[0].get("token") ?? "";
  const [estado, setEstado] = useState<"formulario" | "invalido">(token.length >= 20 ? "formulario" : "invalido");
  const [erro, setErro] = useState<string>();
  const [carregando, setCarregando] = useState(false);
  return (
    <TelaDefinirSenha
      marca={marca()}
      estado={estado}
      erro={erro}
      carregando={carregando}
      aoDefinir={async (senha) => {
        setCarregando(true);
        setErro(undefined);
        try {
          await depois(await publica<RespostaDeEntrada>("/v1/acesso/definir-senha", { token, senha }));
        } catch (err) {
          if (err instanceof ErroApi && err.status === 400 && String(err.corpo?.erro ?? "").includes("link")) setEstado("invalido");
          else setErro(mensagem(err, { 400: "A senha precisa de pelo menos 8 caracteres." }, "Não conseguimos criar a senha agora. Tente de novo."));
        } finally {
          setCarregando(false);
        }
      }}
    />
  );
}
